import { virtualAssistanceVFANoFoveaNoChangingFixationConfig } from "@constants/virtualAssistanceConstants";
import {
  AvailablePreTestConfigurationOptions,
  AvailableProgressPageTestConfigurationOptions,
  EyeType,
  GoldmannSize,
  TestCategory,
  TestConfiguration,
  TestLanguage,
  TestProgressConfiguration,
  TestType,
  TestUIConfiguration,
  VFABehaviour,
  WaitDuration,
} from "types/testTypes";
import { FEATURE_VERSION_MAP, TEST_TYPE_VERSION_MAP } from "../versionConstants";
import {
  AVAILABLE_EYE_OPTIONS_MONOCULAR_BACK2BACK,
  CALIBRATION_TOGGLE_OPTION_NEW_APK,
  AVAILABLE_VIRTUAL_ASSISTANT_OPTIONS,
  DUMMY_AVAILABLE_TEST_SPECIFIC_CONFIGURATION_OPTIONS,
} from "../configurationHelperConstants";
import { englishLanguageData } from "@constants/languageConstants";
import { HeadsetVersion } from "types/productTypes";

export const BINOCULAR_VFA_TEST_TYPES = [
  TestType.PER_BINOCULAR,
  TestType.PER_BINOCULAR2,
  TestType.PER_BINOCULAR3,
] as const;

export const isBinocularVFATestType = (testType?: TestType | string | null): boolean =>
  !!testType && (BINOCULAR_VFA_TEST_TYPES as readonly string[]).includes(testType);

export const SUPRATHRESHOLD_VFA_BEHAVIOURS = [VFABehaviour.SIMPLE_TEST] as const;

export const STANDARD_VFA_TEST_TYPES = [
  TestType.PER_10_2,
  TestType.PER_24_2,
  TestType.PER_24_2C,
  TestType.PER_30_2,
] as const;

export const isStandardVFATestType = (testType?: TestType | string | null): boolean =>
  !!testType && (STANDARD_VFA_TEST_TYPES as readonly string[]).includes(testType);

// Only these patterns have normative data, which the zone styles need.
export const STANDARD_PATTERN_VFA_BEHAVIOURS = [
  VFABehaviour.TOP,
  VFABehaviour.ADAPTIVE_TRACKING,
  VFABehaviour.SITA_LIKE,
  VFABehaviour.FULL_THRESH_4,
  VFABehaviour.SWAP_TOP,
  VFABehaviour.TWO_ZONE,
  VFABehaviour.THREE_ZONE,
] as const;

// The headset tests the 24-2C central points only under these strategies.
const PATTERN_24_2C_VFA_BEHAVIOURS = [
  VFABehaviour.TOP,
  VFABehaviour.ADAPTIVE_TRACKING,
  VFABehaviour.SITA_LIKE,
  VFABehaviour.SWAP_TOP,
] as const;

export const selectableVFABehavioursFor = (
  testType?: TestType | string | null,
): readonly VFABehaviour[] => {
  if (testType === TestType.PER_24_2C) return PATTERN_24_2C_VFA_BEHAVIOURS;
  return isStandardVFATestType(testType)
    ? STANDARD_PATTERN_VFA_BEHAVIOURS
    : SUPRATHRESHOLD_VFA_BEHAVIOURS;
};

export const availableVFATestConfigurationOptions: AvailablePreTestConfigurationOptions = {
  testTypes: {
    configurationFieldName: "testType",
    choices: [
      {
        value: TestType.PER_10_2,
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.PER_10_2],
      },
      {
        value: TestType.PER_24_2,
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.PER_24_2],
      },
      {
        value: TestType.PER_24_2C,
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.PER_24_2C],
      },
      {
        value: TestType.PER_30_2,
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.PER_30_2],
      },
      {
        value: TestType.PER_C_40,
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.PER_C_40],
      },
      {
        value: TestType.PER_C_76,
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.PER_C_76],
      },
      {
        value: TestType.PER_FF_120,
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.PER_FF_120],
      },
      {
        value: TestType.PER_SUPERIOR36,
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.PER_SUPERIOR36],
      },
      {
        value: TestType.PER_SUPERIOR64,
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.PER_SUPERIOR64],
      },
      {
        value: TestType.PER_BINOCULAR,
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.PER_BINOCULAR],
      },
      {
        value: TestType.PER_BINOCULAR2,
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.PER_BINOCULAR2],
      },
      {
        value: TestType.PER_BINOCULAR3,
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.PER_BINOCULAR3],
      },
    ],
    validSinceVersion: null,
  },
  eyeConfigurationOptions: {
    ...AVAILABLE_EYE_OPTIONS_MONOCULAR_BACK2BACK,
    pauseOnFocusLostOption: {
      configurationFieldName: "autoPauseForGaze",
      choices: null,
      validSinceVersion: null,
    },
    toggleTwoScreenFixationOption: {
      configurationFieldName: "isTwoScreenFixationEnabled",
      choices: null,
      validSinceVersion: null,
    },
    toggleCalibrationOption: { ...CALIBRATION_TOGGLE_OPTION_NEW_APK },
  },
  languageOptions: null,
  voiceAssistantOptions: {
    configurationFieldName: "voiceAssistantConfig",
    validSinceVersion: HeadsetVersion.ver_2_6_4,
    choices: [
      { value: TestLanguage.NOT_SPECIFIED },
      { value: TestLanguage.Arabic },
      { value: TestLanguage.Chinese },
      { value: TestLanguage.Czech, validSinceVersion: FEATURE_VERSION_MAP.newLanguages3 },
      { value: TestLanguage.English },
      { value: TestLanguage.French },
      { value: TestLanguage.German, validSinceVersion: FEATURE_VERSION_MAP.newLanguages2 },
      { value: TestLanguage.Italian, validSinceVersion: FEATURE_VERSION_MAP.newLanguages2 },
      { value: TestLanguage.Persian },
      { value: TestLanguage.Romanian, validSinceVersion: FEATURE_VERSION_MAP.newLanguages3 },
      { value: TestLanguage.Spanish },
      { value: TestLanguage.Turkish, validSinceVersion: FEATURE_VERSION_MAP.newLanguages2 },
      { value: TestLanguage.Urdu },
    ],
  },
  virtualAssistantOptions: {
    ...AVAILABLE_VIRTUAL_ASSISTANT_OPTIONS,
    selectorType: "assistantMode",
  },
  testSpeedOptions: {
    configurationFieldName: "testSpeed",
    choices: [
      { value: WaitDuration.Standard, validSinceVersion: null },
      { value: WaitDuration.Slow, validSinceVersion: null },
      { value: WaitDuration.Slower, validSinceVersion: null },
    ],
    validSinceVersion: null,
  },
  testSpecificConfigurationOptions: {
    ...DUMMY_AVAILABLE_TEST_SPECIFIC_CONFIGURATION_OPTIONS,
    vfaBehaviourOptions: {
      choices: [
        {
          value: VFABehaviour.TOP,
          validSinceVersion: null,
        },
        {
          value: VFABehaviour.ADAPTIVE_TRACKING,
          validSinceVersion: FEATURE_VERSION_MAP["vfaAdaptiveTracking"],
        },
        {
          value: VFABehaviour.SITA_LIKE,
          validSinceVersion: null,
        },
        {
          value: VFABehaviour.FULL_THRESH_4,
          validSinceVersion: null,
        },
        {
          value: VFABehaviour.SWAP_TOP,
          validSinceVersion: FEATURE_VERSION_MAP["swap"],
        },
        {
          value: VFABehaviour.SIMPLE_TEST,
          validSinceVersion: null,
        },
        {
          value: VFABehaviour.TWO_ZONE,
          validSinceVersion: FEATURE_VERSION_MAP["vfaZoneTests"],
        },
        {
          value: VFABehaviour.THREE_ZONE,
          validSinceVersion: FEATURE_VERSION_MAP["vfaZoneTests"],
        },
      ],
      validSinceVersion: null,
      configurationFieldName: "testBehaviour",
    },
    toggleBlindSpotTesting: {
      choices: null,
      validSinceVersion: null,
      configurationFieldName: "blindSpotTesting",
    },
    toggleBlindSpotMonitoring: {
      choices: null,
      validSinceVersion: null,
      configurationFieldName: "blindSpotMonitoringEnabled",
    },
    toggleFovealThresholdTesting: {
      choices: null,
      validSinceVersion: FEATURE_VERSION_MAP["vfaFovealThresholdTesting"],
      configurationFieldName: "fovealThresholdTestingEnabled",
    },
    fixationSizeOptions: {
      choices: [
        { value: GoldmannSize.GOLDMANN_III, validSinceVersion: null },
        { value: GoldmannSize.GOLDMANN_IV, validSinceVersion: null },
        { value: GoldmannSize.GOLDMANN_V, validSinceVersion: null },
      ],
      validSinceVersion: null,
      configurationFieldName: "fixationSize",
    },
    stimuliSizeOptions: {
      choices: [
        { value: GoldmannSize.GOLDMANN_III, validSinceVersion: null },
        { value: GoldmannSize.GOLDMANN_IV, validSinceVersion: null },
        { value: GoldmannSize.GOLDMANN_V, validSinceVersion: null },
      ],
      validSinceVersion: null,
      configurationFieldName: "stimuliSize",
    },
  },
};

export const defaultVFATestConfiguration: TestConfiguration = {
  name: TestCategory.VisualField,
  testType: TestType.PER_24_2,
  eyeConfiguration: {
    eyeType: EyeType.RightEye,
    isCalibrationNeeded: true,
    autoPauseForGaze: true,
    isTwoScreenFixationEnabled: true,
    isBackToBackEyeTest: false,
  },
  testSpeed: WaitDuration.Standard,
  voiceAssistantConfig: {
    isVoiceAssistantEnabled: true,
    assistantLanguage: TestLanguage.English,
  },
  virtualAssistanceConfig: {
    ...virtualAssistanceVFANoFoveaNoChangingFixationConfig,
    assistantLanguage: englishLanguageData.languageCode,
  },
  testLanguage: null,
  isPressToStartEnabled: true,
  extraData: {
    testBehaviour: VFABehaviour.TOP,
    blindSpotTesting: true,
    blindSpotMonitoringEnabled: true,
    fovealThresholdTestingEnabled: false,
    enablePausingTheTestWithButton: false,
    showTutorial: false,
    fixationSize: GoldmannSize.GOLDMANN_IV,
    stimuliSize: GoldmannSize.GOLDMANN_III,
  },
};

export const availablVFATestProgressConfigurationOptions: AvailableProgressPageTestConfigurationOptions =
  {
    eyeConfigurationOptions: {
      eyeTypes: null,
      toggleCalibrationOption: null,
      pauseOnFocusLostOption: {
        configurationFieldName: "autoPauseForGaze",
        choices: null,
        validSinceVersion: FEATURE_VERSION_MAP["vfaMidTestGazeAutoPauseToggle"],
      },
      toggleTwoScreenFixationOption: {
        configurationFieldName: "isTwoScreenFixationEnabled",
        choices: null,
        validSinceVersion: null,
      },
    },
    testSpeedOptions: {
      configurationFieldName: "testSpeed",
      choices: [
        { value: WaitDuration.Standard, validSinceVersion: null },
        { value: WaitDuration.Slow, validSinceVersion: null },
        { value: WaitDuration.Slower, validSinceVersion: null },
      ],
      validSinceVersion: null,
    },
  };

export const defaultVFAProgressConfiguration: TestProgressConfiguration = {
  eyeConfiguration: {
    autoPauseForGaze: true,
    isTwoScreenFixationEnabled: true,
  },
  testSpeed: WaitDuration.Standard,
};

export const defaultVFAUIConfiguration: TestUIConfiguration = {
  showProgressBar: true,
  instructions: null,
  isTestOtherEyeOptionPermitted: true,
  isTurnOffDeviceOptionPermitted: true,
  isConfigSavePermitted: true,
};

export const START_COLOR_DB_VFA_STANDARD = 24;
export const START_COLOR_DB_VFA_SWAP = 12;
